import { useContext, useState } from "react";
import { Context } from "../../../context/Context";
import { ActionMenu, Archive, BadgePlus, Copy, Layers, Pencil, Percent, Plus, Utensils, X } from "../../ui";
import { topActionsText } from "./topActionsText";
import ItemActionDialog from "./ItemActionDialog";
import SectionActionDialog from "./SectionActionDialog";
import DiscountActionDialog from "./DiscountActionDialog";
import "./TopActions.css";

// The Item / Section / Discount groups between the "Menu" and "AI"
// dropdowns (Menu.jsx): three labelled (icon + title) dropdowns instead of a button per
// operation. Each entry opens a dialog where the user picks the menu, section
// and item(s), so nothing has to be located on the page first.
// `menus` = the open restaurant's menus (the dialogs only offer non-archived
// ones as destinations); `restaurant` = that restaurant. Copy dialogs can also
// reach other restaurants the backend lists for the user.
export default function MenuTopActions({ menus, restaurant, currentMenuId, onDone, disabled = false }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const [open, setOpen] = useState(null);
  const noMenus = !menus.some((m) => m.lifecycle !== "archived");
  const openDialog = (kind, mode) => () => setOpen({ kind, mode });

  const groups = [
    {
      key: "item",
      label: t.item,
      menuLabel: t.itemGroup,
      icon: <Utensils size={16} />,
      items: [
        { key: "add", label: t.addItem, icon: <Plus size={16} />, onClick: openDialog("item", "add") },
        { key: "update", label: t.updateItem, icon: <Pencil size={16} />, onClick: openDialog("item", "update") },
        { key: "copy", label: t.copyItem, icon: <Copy size={16} />, onClick: openDialog("item", "copy") },
        { key: "archive", label: t.archiveItem, icon: <Archive size={16} />, tone: "danger", divider: true, onClick: openDialog("item", "archive") },
      ],
    },
    {
      key: "section",
      label: t.section,
      menuLabel: t.sectionGroup,
      icon: <Layers size={16} />,
      items: [
        { key: "add", label: t.addSection, icon: <Plus size={16} />, onClick: openDialog("section", "add") },
        { key: "update", label: t.updateSection, icon: <Pencil size={16} />, onClick: openDialog("section", "update") },
        { key: "copy", label: t.copySection, icon: <Copy size={16} />, onClick: openDialog("section", "copy") },
        { key: "archive", label: t.archiveSection, icon: <Archive size={16} />, tone: "danger", divider: true, onClick: openDialog("section", "archive") },
      ],
    },
    {
      key: "discount",
      label: t.discount,
      menuLabel: t.discountGroup,
      icon: <Percent size={16} />,
      items: [
        { key: "add", label: t.addDiscount, icon: <BadgePlus size={16} />, onClick: openDialog("discount", "add") },
        { key: "update", label: t.updateDiscount, icon: <Pencil size={16} />, onClick: openDialog("discount", "update") },
        { key: "copy", label: t.copyDiscount, icon: <Copy size={16} />, onClick: openDialog("discount", "copy") },
        { key: "remove", label: t.removeDiscount, icon: <X size={16} />, tone: "danger", divider: true, onClick: openDialog("discount", "remove") },
      ],
    },
  ];

  const close = () => setOpen(null);
  const done = (result) => {
    setOpen(null);
    onDone(result);
  };
  const props = { menus, restaurant, initialMenuId: currentMenuId, onClose: close, onDone: done };

  return (
    <div className="menu-top-actions" role="group" aria-label={`${t.item} / ${t.section} / ${t.discount}`}>
      {groups.map((group) => (
        <ActionMenu key={group.key} label={group.menuLabel} triggerLabel={group.label} icon={group.icon} items={group.items} disabled={disabled || noMenus} buttonClassName="menu-top-action" />
      ))}
      {open?.kind === "item" && <ItemActionDialog key={`item-${open.mode}`} mode={open.mode} {...props} />}
      {open?.kind === "section" && <SectionActionDialog key={`section-${open.mode}`} mode={open.mode} {...props} />}
      {open?.kind === "discount" && <DiscountActionDialog key={`discount-${open.mode}`} operation={open.mode} {...props} />}
    </div>
  );
}
